<template>
  <div v-if="open" class="omx-cmd" @click.self="close" role="dialog" aria-modal="true">
    <div class="omx-cmd__panel">
      <div class="omx-cmd__input">
        <b-icon icon="magnify" />
        <input
            ref="input"
            v-model="query"
            :placeholder="$t('shell.command.placeholder')"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="trigger"
            @keydown.esc="close"
        />
        <span class="omx-cmd__kbd">esc</span>
      </div>
      <div class="omx-cmd__list" v-if="filtered.length">
        <div
            v-for="(item, i) in filtered"
            :key="item.id"
            class="omx-cmd__item"
            :class="{ 'is-active': i === active }"
            @mouseenter="active = i"
            @click="run(item)"
        >
          <span class="omx-cmd__item-ico">
            <b-icon :icon="item.icon" size="is-small" />
          </span>
          <span class="omx-cmd__item-body">
            <b>{{ item.label }}</b>
            <small v-if="item.hint">{{ item.hint }}</small>
          </span>
          <span class="omx-cmd__item-group">{{ item.group }}</span>
        </div>
      </div>
      <div class="omx-cmd__empty" v-else>
        <span v-if="loadingData">
          <b-icon icon="loading" custom-class="mdi-spin" size="is-small" /> {{ $t('shell.command.searching') }}
        </span>
        <span v-else>{{ $t('shell.command.no-results') }} "<b>{{ query }}</b>"</span>
      </div>
      <div class="omx-cmd__foot">
        <span><kbd>↑</kbd> <kbd>↓</kbd> {{ $t('shell.command.navigate') }}</span>
        <span><kbd>↵</kbd> {{ $t('shell.command.select') }}</span>
        <span><kbd>esc</kbd> {{ $t('shell.command.dismiss') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "OmxCommandPalette",
  props: {
    open: { type: Boolean, default: false },
    loggedIn: { type: Boolean, default: false }
  },
  data() {
    return {
      query: "",
      active: 0,
      // searchable data, lazily loaded on first open (stale-while-revalidate)
      projects: [],
      people: [],
      loadingData: false,
      loaded: false,
      fetchedAt: 0
    };
  },
  computed: {
    items() {
      if (!this.loggedIn) return [];
      const list = [];
      const push = (entry) => list.push(Object.assign({ group: this.$t('shell.command.group-nav') }, entry));

      if (this.currentUserAccess('Projects', this.UserActions.VIEW)) {
        push({ id: 'go-projects',  icon: 'view-grid-outline',  label: this.$t('top-menu.projects'),          to: '/projects' });
      }
      if (this.currentUserAccess('ProjectTemplates', this.UserActions.VIEW)) {
        push({ id: 'go-templates', icon: 'content-duplicate',  label: this.$t('top-menu.project-templates'), to: '/templates' });
      }
      if (this.currentUserAccess('Users', this.UserActions.VIEW)) {
        push({ id: 'go-users',     icon: 'account-group',      label: this.$t('top-menu.users'),             to: '/users' });
      }
      if (this.currentUserAccess('CategoricalData', this.UserActions.VIEW)) {
        push({ id: 'go-cats',      icon: 'shape',              label: this.$t('top-menu.categories'),        to: '/categories' });
      }
      push({ id: 'go-help',      icon: 'help-circle-outline', label: this.$t('top-menu.help'),              to: '/help' });

      // Account-level commands
      const action = this.$t('shell.command.group-action');
      list.push({ id: 'act-account', icon: 'account-cog', label: this.$t('top-menu.account'),       group: action, emit: 'account' });
      list.push({ id: 'act-logout',  icon: 'logout',      label: this.$t('top-menu.sign-out'),      group: action, emit: 'logout' });
      list.push({ id: 'act-lang-fr', icon: 'translate',   label: this.$t('shell.command.lang-fr'),  group: action, hint: 'fr', lang: 'fr' });
      list.push({ id: 'act-lang-en', icon: 'translate',   label: this.$t('shell.command.lang-en'),  group: action, hint: 'en', lang: 'en' });

      // Developer (system only) — demo-only build has no endpoint switcher.

      return list;
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;

      const cmds = this.items.filter(
        it => it.label.toLowerCase().includes(q) || (it.hint || '').toLowerCase().includes(q)
      );

      const projHits = this.projects
        .filter(p => (p.code + ' ' + p.title + ' ' + p.managers).toLowerCase().includes(q))
        .slice(0, 6)
        .map(p => ({
          id: 'proj-' + p.id,
          icon: 'view-grid-outline',
          label: p.title || p.code,
          hint: p.code,
          group: this.$t('shell.command.group-projects'),
          to: '/projects/' + p.id
        }));

      const peopleHits = this.people
        .filter(u => (u.name + ' ' + u.email).toLowerCase().includes(q))
        .slice(0, 6)
        .map(u => ({
          id: 'user-' + u.id,
          icon: 'account',
          label: u.name,
          hint: u.email,
          group: this.$t('shell.command.group-people'),
          to: '/users/' + u.id
        }));

      return [...cmds, ...projHits, ...peopleHits];
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.query = "";
        this.active = 0;
        this.ensureData();
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      }
    },
    filtered() {
      this.active = 0;
    }
  },
  methods: {
    // Lazily load the searchable data the first time the palette opens, then
    // refresh on open at most every 30s (stale-while-revalidate: cached results
    // stay visible while a refresh runs). Only fetch collections the user may view.
    ensureData() {
      if (!this.loggedIn || this.loadingData) return;
      if (this.loaded && Date.now() - this.fetchedAt < 30000) return;

      const canProjects = this.currentUserAccess('Projects', this.UserActions.VIEW);
      const canUsers = this.currentUserAccess('Users', this.UserActions.VIEW);
      if (!canProjects && !canUsers) return;

      this.loadingData = true;
      const call = firebase.app()
          .functions(this.$brand.functionsRegion)
          .httpsCallable(this.getSubmitEndpoint());
      const jobs = [];

      if (canProjects) {
        jobs.push(
            call({ action: "get_projects", fill_meta: true, filters: [["is_archived", "==", false]] })
                .then(r => {
                  this.projects = (r.data.projects || []).map(p => {
                    const meta = p.meta || {};
                    const managers = [...(meta.managers || []), ...(meta.site_managers || [])]
                        .map(m => m && m.name ? this.getFullName(m.name) : "")
                        .join(" ");
                    return { id: p.document_id, code: p.code || "", title: p.title || "", managers };
                  });
                })
                .catch(e => console.log("command palette: get_projects failed", e?.code))
        );
      }

      if (canUsers) {
        jobs.push(
            call({ action: "get_users", fill_meta: true, filters: [["is_archived", "==", false]] })
                .then(r => {
                  this.people = (r.data.users || []).map(u => ({
                    id: u.document_id,
                    name: u.name ? this.getFullName(u.name) : "",
                    email: u.email || ""
                  }));
                })
                .catch(e => console.log("command palette: get_users failed", e?.code))
        );
      }

      Promise.all(jobs).then(() => {
        this.loaded = true;
        this.fetchedAt = Date.now();
        this.loadingData = false;
      });
    },
    close() {
      this.$emit('close');
    },
    move(delta) {
      const len = this.filtered.length;
      if (!len) return;
      this.active = (this.active + delta + len) % len;
    },
    trigger() {
      const item = this.filtered[this.active];
      if (item) this.run(item);
    },
    run(item) {
      if (item.to) {
        if (this.$route.path !== item.to) this.$router.push(item.to);
      } else if (item.lang) {
        this.changeLanguage(item.lang);
      } else if (item.endpoint) {
        this.saveSubmitEndpoint(item.endpoint);
        this.$emit('endpoint-changed', item.endpoint);
      } else if (item.emit) {
        this.$emit(item.emit);
      }
      this.close();
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-cmd {
  position: fixed;
  inset: 0;
  background: rgba(11, 18, 32, .45);
  backdrop-filter: blur(2px);
  z-index: 60;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
  animation: omx-cmd-in .12s ease-out;
}

@keyframes omx-cmd-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: none; }
}

.omx-cmd__panel {
  width: min(620px, 92vw);
  background: $omx-surface;
  border-radius: $omx-radius-lg;
  box-shadow: $omx-shadow-lg;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.omx-cmd__input {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid $omx-ink-100;
  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 15px;
    color: $omx-ink-900;
  }
}
.omx-cmd__kbd {
  font-family: $omx-font-mono;
  font-size: 11px;
  border: 1px solid $omx-ink-200;
  background: $omx-ink-50;
  padding: 1px 6px;
  border-radius: 6px;
  color: $omx-ink-500;
}

.omx-cmd__list {
  overflow-y: auto;
  padding: 6px;
}
.omx-cmd__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  color: $omx-ink-700;
  &.is-active {
    background: $omx-brand-50;
    color: $omx-brand-700;
  }
}
.omx-cmd__item-ico {
  width: 26px; height: 26px;
  border-radius: 7px;
  background: $omx-ink-50;
  color: $omx-ink-500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .is-active & { background: $omx-brand-100; color: $omx-brand-600; }
}
.omx-cmd__item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  b { font-size: 13.5px; font-weight: 600; }
  small { color: $omx-ink-400; font-size: 11.5px; }
}
.omx-cmd__item-group {
  font-size: 10.5px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: $omx-ink-400;
  font-weight: 600;
}

.omx-cmd__empty {
  padding: 24px;
  text-align: center;
  color: $omx-ink-500;
  font-size: 13.5px;
}

.omx-cmd__foot {
  display: flex;
  gap: 16px;
  padding: 10px 14px;
  border-top: 1px solid $omx-ink-100;
  background: $omx-ink-50;
  font-size: 11.5px;
  color: $omx-ink-500;
  kbd {
    background: #fff;
    border: 1px solid $omx-ink-200;
    border-radius: 4px;
    padding: 0 5px;
    font-family: $omx-font-mono;
    font-size: 10.5px;
    color: $omx-ink-700;
    margin-right: 2px;
  }
}
</style>
